/**
 * Snagcart Overview — Getting Started panel config.
 * Paths align with platformNav.js / platformTutorial.js (TUTORIAL_SECTIONS ids).
 */

export const GETTING_STARTED_INTRO =
  'Build sites, scrape leads, market, and run AI — start here.';

/**
 * Demo video: env override, else in-repo pitch cut (public/), else null → open Platform Guide.
 * Set REACT_APP_GETTING_STARTED_VIDEO_URL to a Loom/YouTube/MP4 URL in deploy env when ready.
 */
const ENV_VIDEO =
  (typeof process !== 'undefined' && process.env?.REACT_APP_GETTING_STARTED_VIDEO_URL) || '';

export const GETTING_STARTED_VIDEO = {
  label: 'Snagcart Overview',
  // Overview cut ships under frontend/public/pitch/demo-video/
  href: ENV_VIDEO.trim() || '/pitch/demo-video/snagcart-overview.mp4',
  /** Used when href is empty */
  fallbackPath: '/platform-guide?section=welcome',
};

export const GETTING_STARTED_GUIDES = [
  {
    id: 'first-site',
    label: 'Launch your first website',
    // TUTORIAL_SECTIONS id (not QUICK_WORKFLOWS `first-site`)
    path: '/platform-guide?section=websites',
  },
  {
    id: 'api-setup',
    label: 'API keys & integrations',
    path: '/platform-guide?section=api-setup',
  },
];

export const GETTING_STARTED_STEPS = [
  {
    id: 'launch-site',
    title: 'Launch your first site',
    blurb: 'Template or AI → edit → publish on a Snagcart URL.',
    actions: [
      { label: 'Create website', path: '/websites', navId: 'websites' },
      { label: 'Open Snag Builder', path: '/snagbuilder', navId: 'snagbuilder' },
    ],
  },
  {
    id: 'find-leads',
    title: 'Find and store leads',
    blurb: 'Scrape by niche or location, then sync into CRM.',
    actions: [
      { label: 'Lead Scraper', path: '/lead-scraper', navId: 'lead-scraper' },
      { label: 'CRM', path: '/marketing-suite/crm', navId: 'crm' },
    ],
  },
  {
    id: 'reach-customers',
    title: 'Reach customers',
    blurb: 'Email, WhatsApp, and SMS from Marketing Suite.',
    actions: [
      { label: 'Marketing Suite', path: '/marketing-suite', navId: 'marketing' },
      { label: 'WhatsApp', path: '/marketing-suite/whatsapp', navId: 'marketing' },
    ],
  },
  {
    id: 'ai-cofounder',
    title: 'Run AI Co-founder',
    blurb: 'Autopilot roadmap across CRM, Builder, SEO, and social.',
    actions: [
      { label: 'AI Co-founder', path: '/ai-cofounder', navId: 'ai-cofounder' },
      { label: 'Outputs', path: '/ai-cofounder/outputs', navId: 'ai-cofounder' },
    ],
  },
  {
    id: 'unlock-platform',
    title: 'Unlock the platform',
    blurb: 'API keys and Connections so modules can send and publish.',
    actions: [
      { label: 'API Keys', path: '/settings?tab=api-keys', navId: 'settings' },
      { label: 'Connections', path: '/connections', navId: 'connections' },
    ],
  },
];

export const GETTING_STARTED_TILES = [
  {
    id: 'import-contacts',
    title: 'Import contacts',
    blurb: 'Seed CRM without scraping — upload a CSV.',
    cta: 'Open CRM',
    path: '/marketing-suite/crm',
    navId: 'crm',
  },
  {
    id: 'connect-sources',
    title: 'Connect data sources',
    blurb: 'Gmail, Google, Evolution, and other OAuth links.',
    cta: 'Open Connections',
    path: '/connections',
    navId: 'connections',
  },
  {
    id: 'set-domain',
    title: 'Set your domain',
    blurb: 'Search, register, or point a custom domain.',
    cta: 'Domains & Hosting',
    path: '/domains-hosting',
    navId: 'domains',
  },
];

/**
 * Explore pillars — every PLATFORM_NAV_ITEMS id except overview (you are already there).
 * Keep in sync when sidebar modules change.
 */
export const EXPLORE_PILLARS = [
  {
    id: 'build',
    label: 'Build',
    navIds: ['websites', 'create-template', 'snagbuilder', 'domains'],
  },
  {
    id: 'ai',
    label: 'AI',
    navIds: ['ai-cofounder', 'ai-tools', 'media-studio', 'ai-studio'],
  },
  {
    id: 'grow',
    label: 'Grow',
    navIds: ['marketing', 'ai-seo', 'ai-ads', 'crm', 'lead-scraper', 'bot-leads', 'voice'],
  },
  {
    id: 'operate',
    label: 'Operate',
    navIds: ['automations', 'connections'],
  },
  {
    id: 'account',
    label: 'Account',
    navIds: ['overview', 'platform-guide', 'support', 'odoo-erp', 'subscription-plans'],
  },
];
